import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/auth';
import type { User } from '../types';

interface RegisterData {
  fullName: string;
  email: string;
  password: string;
  phone?: string;
  address?: string;
  zipCode?: string;
  birthDate?: string;
  gender: string;
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (emailOrUsername: string, password: string) => Promise<User>;
  register: (userData: RegisterData) => Promise<User>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Restore from localStorage immediately to avoid flash of unauthenticated state
    const stored = authService.getCurrentUser();
    if (stored) {
      setUser(stored);
      // Background refresh to keep profile data current
      void authService.refreshProfile().then((fresh) => {
        if (fresh) setUser(fresh);
      });
    }
    setLoading(false);
  }, []);

  const login = async (emailOrUsername: string, password: string): Promise<User> => {
    const { user: loggedInUser } = await authService.login(emailOrUsername, password);
    setUser(loggedInUser);
    return loggedInUser;
  };

  const register = async (_userData: RegisterData): Promise<User> => {
    await authService.register();
    // register() always throws — this line is unreachable
    throw new Error('Registration not supported');
  };

  const logout = async (): Promise<void> => {
    await authService.logout();
    setUser(null);
  };

  const value: AuthContextValue = {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

